import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faGear, faShuffle } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { buildSnippetToken } from "../../AiSnippets/utils/snippetTokens";
import { SpintaxRow } from "../utils/spintaxRows";

interface SpintaxSectionRowProps {
	row: SpintaxRow;
	onInsert: (row: SpintaxRow) => void;
	onConfigure: (row: SpintaxRow) => void;
}

/**
 * One row of the spintax section: shuffle icon, column name, variation count,
 * an "In message" / "Insert" status badge and a gear that opens the column
 * configuration. Clicking the row inserts the spintax placeholder at the
 * editor cursor; rows can also be dragged into the editor.
 */
export const SpintaxSectionRow: FunctionComponent<SpintaxSectionRowProps> = ({
	row,
	onInsert,
	onConfigure,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className="group w-full flex items-center gap-2 px-2.5 py-2 rounded-md border border-transparent text-left transition-colors cursor-pointer hover:bg-background-light hover:border-border-light"
			data-column-id={row.columnId}
			data-column-name={row.name}
			draggable
			role="button"
			onClick={() => onInsert(row)}
			onDragStart={(e: React.DragEvent<HTMLDivElement>) =>
				e.dataTransfer?.setData("text", buildSnippetToken(row.columnId, row.name))
			}
		>
			<span className="w-[22px] h-[22px] flex-shrink-0 rounded bg-spintax-light text-spintax flex items-center justify-center">
				<Icon className="w-3 h-3" icon={faShuffle} />
			</span>

			<span className="flex-shrink min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] font-medium text-text">
				{row.name}
			</span>

			<span className="flex-shrink-0 whitespace-nowrap text-xs text-text-meta">
				{t("spintax.section.variations", { count: row.variationCount })}
			</span>

			<span className="flex-1 min-w-2" />

			<span
				className={classNames(
					"flex-shrink-0 text-xxs font-medium px-1.5 py-0.5 rounded",
					row.isInserted
						? "bg-status-success-light text-status-success"
						: "bg-background-light text-text-meta"
				)}
			>
				{row.isInserted ? t("spintax.section.inMessage") : t("spintax.section.insert")}
			</span>

			<button
				aria-label={t("spintax.section.configure")}
				className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded border-0 bg-transparent cursor-pointer text-text-meta hover:bg-border-light hover:text-text"
				title={t("spintax.section.configure")}
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onConfigure(row);
				}}
			>
				<Icon className="w-3.5 h-3.5" icon={faGear} />
			</button>
		</div>
	);
};
